import { Checkbox, CheckboxProps } from '@unistocks/components/checkbox/checkbox';
import { HelpText } from '@unistocks/components/form/components/help-text';
import {
  FormFieldInjectedProps,
  withValidation
} from '@unistocks/components/form/components/with-label-and-validation';
import { FormComponentPropsBase, FormFieldOmittedProps, FormFieldTransform } from '@unistocks/components/form/model';
import { isFunction } from '@unistocks/shared/utils/util';
import { PropsWithRef } from '@unistocks/types';
import { memo } from 'react';
import { FieldValues } from 'react-hook-form';
import { CheckedState, ListItem, useComposedRefs } from 'tamagui';

export type FormCheckboxProps<TFieldValues extends FieldValues = FieldValues> = PropsWithRef<
  Omit<CheckboxProps, FormFieldOmittedProps | 'checked'> &
    Omit<FormComponentPropsBase<TFieldValues>, 'valueTransform'> & {
      valueTransform?: FormFieldTransform<any, CheckedState>;
    },
  typeof ListItem
>;
export type CheckboxRef = FormCheckboxProps['ref'];

const FormCheckboxComponent = memo<FormCheckboxProps & FormFieldInjectedProps>(
  ({
    ref: refProp,
    id,
    name,
    label,
    helpText,
    isRequired,
    isDisabled,
    valueTransform,
    onCheckedChange,
    field,
    fieldState,
    dependsOn,
    ...props
  }) => {
    const composedRef = useComposedRefs(refProp, field.ref);

    const isChecked = valueTransform?.input?.(field.value) ?? field.value;

    const onValueChange = (checked: CheckedState) => {
      let transformedValue = checked as boolean;
      if (isFunction(valueTransform?.output)) {
        transformedValue = valueTransform.output(checked) as boolean;
      }

      field.onChange(transformedValue);
      onCheckedChange?.(checked as boolean);
    };

    return (
      <Checkbox
        ref={composedRef}
        checked={isChecked}
        disabled={isDisabled}
        onCheckedChange={onValueChange}
        label={label}
        iconAfter={helpText ? <HelpText text={helpText} title={label} /> : undefined}
        {...props}
      />
    );
  }
);

FormCheckboxComponent.displayName = 'FormCheckbox';

export const FormCheckbox = withValidation<FormCheckboxProps, CheckboxRef>(FormCheckboxComponent, {
  mb: 0
}) as <TFieldValues extends FieldValues = FieldValues>(
  props: FormCheckboxProps<TFieldValues>
) => React.ReactElement<FormCheckboxProps<TFieldValues>>;
